import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { createPortalApi } from '../../api/portalApi';
import { useRuntimeConfig } from '../../context/RuntimeConfigContext';
import { useUserRole } from '../../context/UserRoleContext';
import { useToast } from '../../components/Toast';
import Field from '../../components/settings/Field';
import type { UserSettings } from '../../types';

export default function MyTestLinkSettingsPage() {
  const { instance } = useMsal();
  const cfg           = useRuntimeConfig();
  const { isAdmin }   = useUserRole();
  const api            = createPortalApi(instance, cfg.msalClientId, cfg.apiBaseUrl);
  const { showToast }  = useToast();

  const [userSettings, setUserSettings]       = useState<UserSettings>({});
  const [loading, setLoading]                 = useState(true);
  const [saving, setSaving]                   = useState(false);
  const [error, setError]                     = useState('');
  const [orgUsesTestLink, setOrgUsesTestLink]  = useState<boolean | null>(null);

  useEffect(() => {
    const promises: Promise<unknown>[] = [
      api.getUserSettings().then(u => setUserSettings(u ?? {})),
    ];
    if (isAdmin) {
      promises.push(api.getSettings().then(s => setOrgUsesTestLink(s?.targetType === 'testlink')));
    } else {
      setOrgUsesTestLink(false);
    }
    Promise.all(promises)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.saveUserSettings(userSettings);
      showToast('Settings saved successfully.', 'success');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="page-loading"><span className="spinner" />Loading settings…</div>;

  if (orgUsesTestLink === false) return <Navigate to="/settings/profile" replace />;

  return (
    <div>
      <Link to="/settings/profile" className="settings-back-link">← My Settings</Link>

      {error && <div className="alert alert-error">{error}</div>}

      <form className="settings-form" onSubmit={handleSubmit}>
        <div className="settings-card">
          <h2 className="settings-section-title">My TestLink Profile</h2>
          <p className="settings-section-desc">
            Your personal TestLink username. Test cases you push will be authored under this login.
            Defaults to <code>admin</code> if left blank.
          </p>
          <div className="form-grid">
            <Field
              label="Your TestLink Username"
              placeholder="e.g. john.doe"
              value={userSettings.testLinkLogin ?? ''}
              onChange={v => setUserSettings(u => ({ ...u, testLinkLogin: v }))}
            />
          </div>
        </div>

        <div className="settings-footer">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}
